import * as THREE from 'three';
import type { LevelCrossing, RailPlatform, World } from '../../shared/types.js';
import type { Heightfield } from '../../shared/heightfield.js';
import { DECK_DEPTH, type DeckIndex } from '../../shared/decks.js';
import {
  GAUGE,
  NARROW_GAUGE,
  RAIL_HEAD,
  RAIL_TOP,
  drapeRailLines,
  type DrapedLine,
} from '../../shared/railway.js';
import { ribbon } from '../../shared/geom.js';
import { hashUnit } from '../../shared/rng.js';

/**
 * The railway that does not move: ballast, sleepers and rails, track set into the street,
 * platforms, the masts of the overhead line, and the posts, crosses and lamp housings at the
 * level crossings. What does move — trains, barrier booms, the lamps themselves — is in
 * `trains.ts`. DECISIONS.md §95.
 *
 * One merged mesh and one material, like the road (§83): what a triangle is — ballast, a
 * sleeper, rail steel, a crossing panel, a platform — is a per-vertex tag, and the sleepers
 * and stones are drawn in the shader rather than built. A sleeper every 0.6 m as geometry
 * would be eighty thousand triangles for Leszno's yards alone; as a stripe it is nothing,
 * and filtered over the pixel it does not crawl at the far end of a straight. The rails are
 * geometry, because a rail is the one thing on a track whose edge catches the light.
 */

export const RAIL_SURFACE = {
  bedConcrete: 0,
  bedTimber: 1,
  shoulder: 2,
  railTop: 3,
  railSide: 4,
  panel: 5,
  platform: 6,
  platformEdge: 7,
  plain: 8,
} as const;

/** Half the top of the ballast bed: a 2.6 m sleeper and a little stone either side. */
const BED_HALF = 1.45;
/** And how far out the shoulder runs as it falls to the ground. */
const SHOULDER = 0.95;
const PANEL_HALF = 1.4;
const RAIL_HEAD_WIDTH = 0.072;

const BALLAST = 0x7e786f;
const BALLAST_SIDING = 0x6e665c;
const STEEL = 0x6a5a4e;
const PANEL = 0x5d5d5b;
const PLATFORM = 0xa9a69f;
const PLATFORM_WALL = 0x8e8a82;
const MAST = 0x8a9095;
const POST = 0x9a9ea2;
const DARK = 0x1c1c1e;
const CROSS_RED = 0xb3271f;
const CROSS_WHITE = 0xe8e6e0;
const DRIVE = 0x8d8f86;

/** Electrified lines get a mast this often, on whichever side has room. */
const MAST_SPACING = 55;
/** A mast stands this far from the track it carries the wire for. */
const MAST_OFFSET = 3.3;
/** Contact wire and catenary over the rail head. */
const CONTACT_WIRE = 5.5;
const CATENARY = 6.7;

/** Platform top over the rail head: 0.55 m, the old Polish standard most stations still have. */
const PLATFORM_ABOVE_RAIL = 0.55;
/** A platform mapped as a line is given this width. */
const LINE_PLATFORM_WIDTH = 5.5;
/** The white line and the tactile strip back from a platform's edge. */
const EDGE_STRIP = 0.9;

export interface RailwayMeshes {
  objects: THREE.Object3D[];
  /** The one surface material, for the sky field and the street lights to patch. */
  material: THREE.MeshStandardMaterial;
  triangles: number;
  setWet(amount: number): void;
  dispose(): void;
}

export function buildRailways(world: World, terrain: Heightfield, decks: DeckIndex): RailwayMeshes | null {
  const rail = world.rail;
  if (!rail) return null;
  const draped = drapeRailLines(world, { terrain, decks });
  const b = new RailBuilder();

  for (const d of draped) {
    if (d.hidden || d.pts.length < 4) continue;
    addTrack(b, d);
  }
  addMasts(b, draped, decks);
  for (const platform of rail.platforms) addPlatform(b, platform, terrain);
  for (const crossing of rail.crossings) {
    for (const post of crossingGear(crossing, terrain)) addCrossingPost(b, post);
  }
  if (!b.count) return null;

  const geometry = b.toGeometry();
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0 });
  // Above the ground and the areas, beside the road: a flush rail at a crossing is a few
  // centimetres over the tarmac, which the offset makes sure the depth test agrees with.
  material.polygonOffset = true;
  material.polygonOffsetFactor = -1;
  material.polygonOffsetUnits = -2;
  const shader = applyRailShader(material);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'railways';
  mesh.receiveShadow = true;
  // Masts and crossing posts cast; the track's own relief is too small to be worth the pass.
  mesh.castShadow = true;
  mesh.matrixAutoUpdate = false;

  const objects: THREE.Object3D[] = [mesh];
  let triangles = b.count / 3;
  const disposables: { dispose(): void }[] = [geometry, material];

  const wires = buildWires(draped, decks);
  if (wires) {
    objects.push(wires.object);
    disposables.push(wires.object.geometry, wires.object.material as THREE.Material);
  }

  return {
    objects,
    material,
    triangles,
    setWet: shader.setWet,
    dispose() {
      for (const item of disposables) item.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// track
// ---------------------------------------------------------------------------

function addTrack(b: RailBuilder, d: DrapedLine): void {
  const n = d.pts.length / 2;
  const gauge = d.line.kind === 'narrow_gauge' ? NARROW_GAUGE : GAUGE;
  const timber = !!d.line.siding;
  const ballast = timber ? BALLAST_SIDING : BALLAST;
  const sideOf = mitredNormals(d.pts);
  // Where two tracks part at a set of points their beds lie over each other for thirty
  // metres, and at one height the two sleeper patterns took turns as the camera moved. A step
  // of a few millimetres per line settles which is on top; the rails stand on their own.
  const bedLift = Math.floor(hashUnit(7, d.line.id) * 5) * 0.005;
  let along = 0;
  for (let i = 1; i < n; i++) {
    const a = i - 1;
    const ax = d.pts[a * 2];
    const az = d.pts[a * 2 + 1];
    const bx = d.pts[i * 2];
    const bz = d.pts[i * 2 + 1];
    const length = Math.hypot(bx - ax, bz - az);
    if (length < 1e-4) continue;
    const v0 = along;
    const v1 = along + length;
    along = v1;
    const na = sideOf[a];
    const nb = sideOf[i];
    const sleeperA = d.top[a] - lerp(RAIL_HEAD - bedLift, 0.025, d.flush[a]);
    const sleeperB = d.top[i] - lerp(RAIL_HEAD - bedLift, 0.025, d.flush[i]);
    const flush = Math.max(d.flush[a], d.flush[i]) > 0.5;

    const at = (side: number, h: number, which: 'a' | 'b', off: number): [number, number, number] => {
      const n2 = which === 'a' ? na : nb;
      const cx = which === 'a' ? ax : bx;
      const cz = which === 'a' ? az : bz;
      return [cx + n2.x * off * side, h, cz + n2.z * off * side];
    };

    const up: V3 = [0, 1, 0];
    if (flush) {
      // Set in the street: one panel across, flush with what is around it.
      b.quadFacing(
        at(-1, sleeperA, 'a', PANEL_HALF), at(1, sleeperA, 'a', PANEL_HALF),
        at(1, sleeperB, 'b', PANEL_HALF), at(-1, sleeperB, 'b', PANEL_HALF), up,
        PANEL, RAIL_SURFACE.panel, [-PANEL_HALF, v0], [PANEL_HALF, v0], [PANEL_HALF, v1], [-PANEL_HALF, v1],
      );
    } else {
      const surface = timber ? RAIL_SURFACE.bedTimber : RAIL_SURFACE.bedConcrete;
      b.quadFacing(
        at(-1, sleeperA, 'a', BED_HALF), at(1, sleeperA, 'a', BED_HALF),
        at(1, sleeperB, 'b', BED_HALF), at(-1, sleeperB, 'b', BED_HALF), up,
        ballast, surface, [-BED_HALF, v0], [BED_HALF, v0], [BED_HALF, v1], [-BED_HALF, v1],
      );
      // The shoulders run down into the ground a little, so a slope under them never shows
      // a slot of daylight.
      const footA = d.base[a] - 0.08;
      const footB = d.base[i] - 0.08;
      const out = BED_HALF + SHOULDER;
      for (const side of [-1, 1]) {
        b.quadFacing(
          at(side, sleeperA, 'a', BED_HALF), at(side, footA, 'a', out), at(side, footB, 'b', out), at(side, sleeperB, 'b', BED_HALF),
          [na.x * side, 1, na.z * side], shade(ballast, 0.88), RAIL_SURFACE.shoulder,
          [side * BED_HALF, v0], [side * out, v0], [side * out, v1], [side * BED_HALF, v1],
        );
      }
    }

    // The two rails: a head, and its two faces down to the sleepers.
    for (const side of [-1, 1]) {
      const inner = gauge / 2 - RAIL_HEAD_WIDTH / 2;
      const outer = gauge / 2 + RAIL_HEAD_WIDTH / 2;
      const p = (off: number, h: number, which: 'a' | 'b') => at(side, h, which, off);
      const ta = d.top[a];
      const tb = d.top[i];
      b.quadFacing(p(inner, ta, 'a'), p(outer, ta, 'a'), p(outer, tb, 'b'), p(inner, tb, 'b'), up, STEEL, RAIL_SURFACE.railTop);
      for (const off of [inner, outer]) {
        // Each face looks away from the rail's middle.
        const away = off === outer ? side : -side;
        b.quadFacing(
          p(off, ta, 'a'), p(off, sleeperA, 'a'), p(off, sleeperB, 'b'), p(off, tb, 'b'),
          [na.x * away, 0, na.z * away], STEEL, RAIL_SURFACE.railSide,
        );
      }
    }
  }
}

/** Unit normals at each point, mitred so a bend keeps its width, clamped like `ribbon`'s. */
function mitredNormals(pts: number[]): { x: number; z: number }[] {
  const n = pts.length / 2;
  const out: { x: number; z: number }[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = Math.max(0, i - 1);
    const p1 = Math.min(n - 1, i + 1);
    let tx = 0;
    let tz = 0;
    for (const [from, to] of [[p0, i], [i, p1]] as const) {
      if (from === to) continue;
      const dx = pts[to * 2] - pts[from * 2];
      const dz = pts[to * 2 + 1] - pts[from * 2 + 1];
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      tx += dx / len;
      tz += dz / len;
    }
    const len = Math.hypot(tx, tz) || 1;
    tx /= len;
    tz /= len;
    // Mitre: the offset grows as the bend sharpens, to a limit.
    let scale = 1;
    if (i > 0 && i < n - 1) {
      const dx = pts[(i + 1) * 2] - pts[i * 2];
      const dz = pts[(i + 1) * 2 + 1] - pts[i * 2 + 1];
      const l = Math.hypot(dx, dz) || 1;
      const cos = (dx / l) * tx + (dz / l) * tz;
      scale = Math.min(2, 1 / Math.max(0.5, cos));
    }
    out.push({ x: -tz * scale, z: tx * scale });
  }
  return out;
}

// ---------------------------------------------------------------------------
// the overhead line
// ---------------------------------------------------------------------------

/** A point along a draped line, `s` metres from its start, with the direction it runs in. */
interface LinePoint {
  x: number;
  z: number;
  ux: number;
  uz: number;
  top: number;
  base: number;
  flush: number;
}

/** Walks a draped line by distance. `along` is how far along each of its points is. */
function lineWalker(d: DrapedLine): { length: number; along: number[]; at(s: number): LinePoint } {
  const n = d.pts.length / 2;
  const along = [0];
  for (let i = 1; i < n; i++) {
    along.push(along[i - 1] + Math.hypot(d.pts[i * 2] - d.pts[i * 2 - 2], d.pts[i * 2 + 1] - d.pts[i * 2 - 1]));
  }
  let i = 1;
  return {
    length: along[n - 1],
    along,
    at(s) {
      // Callers walk forwards and back a little, so start from where the last one was.
      while (i > 1 && along[i - 1] > s) i--;
      while (i < n - 1 && along[i] < s) i++;
      const span = along[i] - along[i - 1];
      const f = span > 1e-9 ? Math.min(1, Math.max(0, (s - along[i - 1]) / span)) : 0;
      const ax = d.pts[i * 2 - 2];
      const az = d.pts[i * 2 - 1];
      const bx = d.pts[i * 2];
      const bz = d.pts[i * 2 + 1];
      const len = Math.hypot(bx - ax, bz - az) || 1;
      return {
        x: ax + (bx - ax) * f,
        z: az + (bz - az) * f,
        ux: (bx - ax) / len,
        uz: (bz - az) / len,
        top: lerp(d.top[i - 1], d.top[i], f),
        base: lerp(d.base[i - 1], d.base[i], f),
        flush: lerp(d.flush[i - 1], d.flush[i], f),
      };
    },
  };
}

/** The wired tracks: electrified, above ground, and not a tram's, which hangs from span wires. */
function wiredLines(draped: DrapedLine[]): DrapedLine[] {
  return draped.filter((d) => d.line.electrified && !d.hidden && !d.line.embedded && d.line.kind !== 'tram' && d.pts.length >= 4);
}

/**
 * How far either way of its place in the spacing a mast may move to get out from under a
 * bridge, and in what steps.
 */
const MAST_SHIFT = 30;
const MAST_SHIFT_STEP = 5;
/** Clearance kept between a mast, or the arm it holds out, and the edge of a deck. */
const MAST_DECK_CLEAR = 1;

/**
 * Where the masts go: every `MAST_SPACING` along each wired track, on the side with room, and
 * never under a bridge. A mast is taller than the clearance under any deck, so one that fell
 * under a viaduct stood up through its road; it moves along the line to the nearest place
 * clear of the deck, as a real one is set either side of the bridge instead.
 */
function mastPositions(
  draped: DrapedLine[],
  decks: DeckIndex,
): { x: number; y: number; z: number; ox: number; oz: number; top: number }[] {
  const wired = wiredLines(draped);
  if (!wired.length) return [];
  // Every track's centreline, to keep a mast out of the next track's way.
  const segs: number[][] = [];
  for (const d of draped) {
    if (d.hidden) continue;
    for (let i = 2; i < d.pts.length; i += 2) segs.push([d.pts[i - 2], d.pts[i - 1], d.pts[i], d.pts[i + 1]]);
  }
  const clear = (x: number, z: number) => {
    for (const [ax, az, bx, bz] of segs) {
      const vx = bx - ax;
      const vz = bz - az;
      const len2 = vx * vx + vz * vz;
      if (Math.abs(x - ax) > 40 && Math.abs(x - bx) > 40) continue;
      let t = len2 > 1e-9 ? ((x - ax) * vx + (z - az) * vz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      if ((x - ax - vx * t) ** 2 + (z - az - vz * t) ** 2 < 2.4 * 2.4) return false;
    }
    return true;
  };
  // Something over the mast or over the track it reaches out to, higher than the track's own
  // bridge if it is on one.
  const overhead = (x: number, z: number, top: number) => decks.ceilingAt(x, z, top + 2, MAST_DECK_CLEAR) !== null;

  const out: { x: number; y: number; z: number; ox: number; oz: number; top: number }[] = [];
  for (const d of wired) {
    const walk = lineWalker(d);
    for (let next = MAST_SPACING / 2; next < walk.length; next += MAST_SPACING) {
      let placed = false;
      for (let k = 0; k <= (2 * MAST_SHIFT) / MAST_SHIFT_STEP && !placed; k++) {
        // 0, +5, -5, +10, -10 …
        const shift = (k % 2 === 1 ? 1 : -1) * Math.ceil(k / 2) * MAST_SHIFT_STEP;
        const s = next + shift;
        if (s < 0 || s > walk.length) continue;
        const p = walk.at(s);
        // Not in a street, where the overhead line hangs from span wires.
        if (p.flush > 0) continue;
        if (overhead(p.x, p.z, p.top)) continue;
        for (const side of [1, -1]) {
          const ox = -p.uz * side;
          const oz = p.ux * side;
          const mx = p.x + ox * MAST_OFFSET;
          const mz = p.z + oz * MAST_OFFSET;
          if (!clear(mx, mz) || overhead(mx, mz, p.top)) continue;
          out.push({ x: mx, y: p.base, z: mz, ox: -ox, oz: -oz, top: p.top });
          placed = true;
          break;
        }
      }
    }
  }
  return out;
}

function addMasts(b: RailBuilder, draped: DrapedLine[], decks: DeckIndex): void {
  for (const m of mastPositions(draped, decks)) {
    const height = m.top - m.y + CATENARY + 0.4;
    b.box(m.x, m.y - 0.3, m.z, 0.24, height + 0.3, 0.24, m.ox, m.oz, MAST);
    // The cantilever out over the track, at the contact wire's height.
    const reach = MAST_OFFSET + 0.4;
    const armY = m.top + CONTACT_WIRE + 0.15;
    b.box(m.x + m.ox * reach / 2, armY, m.z + m.oz * reach / 2, 0.09, 0.09, reach, m.ox, m.oz, MAST);
    b.box(m.x + m.ox * reach / 2, m.top + CATENARY - 0.05, m.z + m.oz * reach / 2, 0.07, 0.07, reach, m.ox, m.oz, MAST);
  }
}

/** Where the wires are worked out along a line: often enough for the dip under a bridge to be a curve. */
const WIRE_STEP = 4;
/**
 * Under a deck: air between the messenger and the soffit, and between the messenger and the
 * contact wire once it is squeezed down onto it.
 */
const WIRE_AIR = 0.3;
const WIRE_SQUEEZE = 0.3;
/**
 * How steeply each wire may come down to pass under a bridge. The contact wire is the one a
 * pantograph runs along, so it is let down gently; the messenger only has to hang over it.
 */
const CONTACT_GRADE = 0.03;
const MESSENGER_GRADE = 0.12;

/**
 * The contact wire and the catenary over it, as lines: a pixel wide at any distance, and
 * nothing to alias.
 *
 * Under a bridge both come down to pass beneath the deck, as a real overhead line is let down
 * under one. They used to run straight on at their full height, through the deck of the Leszno
 * viaduct and out of its road.
 */
function buildWires(draped: DrapedLine[], decks: DeckIndex): { object: THREE.LineSegments } | null {
  const positions: number[] = [];
  for (const d of wiredLines(draped)) {
    const walk = lineWalker(d);
    // The line's own points, so a curve is followed as the track follows it, and enough
    // between them for the dip under a bridge to be a curve rather than a corner.
    const at: number[] = [];
    for (let i = 0; i < walk.along.length; i++) {
      const from = walk.along[i];
      const to = walk.along[i + 1] ?? from;
      const steps = Math.max(1, Math.ceil((to - from) / WIRE_STEP));
      for (let k = 0; k < (i + 1 < walk.along.length ? steps : 1); k++) at.push(from + ((to - from) * k) / steps);
    }
    const points = at.map((s) => walk.at(s));
    // Over a level crossing too: the wire does not stop for the road, only the masts do.
    const contact = points.map(() => CONTACT_WIRE);
    const messenger = points.map(() => CATENARY);
    points.forEach((p, k) => {
      const deck = decks.ceilingAt(p.x, p.z, p.top + 2, MAST_DECK_CLEAR);
      if (deck === null) return;
      const room = deck - DECK_DEPTH - p.top - WIRE_AIR;
      messenger[k] = Math.min(messenger[k], room);
      contact[k] = Math.min(contact[k], room - WIRE_SQUEEZE);
    });
    for (const [wire, grade] of [[contact, CONTACT_GRADE], [messenger, MESSENGER_GRADE]] as const) {
      for (let k = 1; k < wire.length; k++) wire[k] = Math.min(wire[k], wire[k - 1] + grade * (at[k] - at[k - 1]));
      for (let k = wire.length - 2; k >= 0; k--) wire[k] = Math.min(wire[k], wire[k + 1] + grade * (at[k + 1] - at[k]));
    }
    for (let k = 1; k < points.length; k++) {
      const a = points[k - 1];
      const b = points[k];
      positions.push(a.x, a.top + contact[k - 1], a.z, b.x, b.top + contact[k], b.z);
      positions.push(a.x, a.top + messenger[k - 1], a.z, b.x, b.top + messenger[k], b.z);
    }
  }
  if (!positions.length) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeBoundingSphere();
  const material = new THREE.LineBasicMaterial({ color: 0x26282a });
  const object = new THREE.LineSegments(geometry, material);
  object.name = 'rail-wires';
  object.matrixAutoUpdate = false;
  return { object };
}

// ---------------------------------------------------------------------------
// platforms
// ---------------------------------------------------------------------------

function addPlatform(b: RailBuilder, platform: RailPlatform, terrain: Heightfield): void {
  let ring = platform.pts;
  if (!platform.closed) {
    const rib = ribbon(platform.pts, LINE_PLATFORM_WIDTH, 0).positions;
    const left: number[] = [];
    const right: number[] = [];
    for (let i = 0; i < rib.length; i += 6) {
      left.push(rib[i], rib[i + 2]);
      right.push(rib[i + 3], rib[i + 5]);
    }
    ring = [...left];
    for (let i = right.length - 2; i >= 0; i -= 2) ring.push(right[i], right[i + 1]);
    if (signedArea(ring) < 0) ring = reverseRing(ring);
  }
  const n = ring.length / 2;
  if (n < 3) return;
  let low = Infinity;
  for (let i = 0; i < n; i++) low = Math.min(low, terrain.heightAt(ring[i * 2], ring[i * 2 + 1]));
  const top = low + RAIL_TOP + PLATFORM_ABOVE_RAIL;

  const contour = [];
  for (let i = 0; i < n; i++) contour.push(new THREE.Vector2(ring[i * 2], ring[i * 2 + 1]));
  const tris = THREE.ShapeUtils.triangulateShape(contour, []);
  for (const [i, j, k] of tris) {
    const pa: V3 = [ring[i * 2], top, ring[i * 2 + 1]];
    const pb: V3 = [ring[j * 2], top, ring[j * 2 + 1]];
    const pc: V3 = [ring[k * 2], top, ring[k * 2 + 1]];
    // Upward-facing whichever way the triangulation wound it.
    const upward = (pb[2] - pa[2]) * (pc[0] - pa[0]) - (pb[0] - pa[0]) * (pc[2] - pa[2]) > 0;
    const [q1, q2] = upward ? [pb, pc] : [pc, pb];
    b.tri(pa, q1, q2, PLATFORM, RAIL_SURFACE.platform, [pa[0], pa[2]], [q1[0], q1[2]], [q2[0], q2[2]]);
  }
  // Walls, and the strip along each edge. The ring is counter-clockwise in plan (x east,
  // z south), so the outward side of edge a→b is (dz, -dx)... measured, not assumed.
  const ccw = signedArea(ring) > 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = ring[i * 2];
    const az = ring[i * 2 + 1];
    const bx = ring[j * 2];
    const bz = ring[j * 2 + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 0.05) continue;
    let ox = (bz - az) / len;
    let oz = -(bx - ax) / len;
    if (!ccw) {
      ox = -ox;
      oz = -oz;
    }
    const foot = low - 0.3;
    b.quadFacing([ax, top, az], [bx, top, bz], [bx, foot, bz], [ax, foot, az], [ox, 0, oz], PLATFORM_WALL, RAIL_SURFACE.plain);
    if (len < EDGE_STRIP * 2) continue;
    const ix = -ox * EDGE_STRIP;
    const iz = -oz * EDGE_STRIP;
    // Two centimetres proud: at six millimetres the strip and the slab under it fought from
    // a couple of hundred metres off.
    const y = top + 0.02;
    b.quadFacing(
      [ax, y, az], [bx, y, bz], [bx + ix, y, bz + iz], [ax + ix, y, az + iz], [0, 1, 0], PLATFORM, RAIL_SURFACE.platformEdge,
      [0, 0], [0, len], [1, len], [1, 0],
    );
  }
}

function signedArea(ring: number[]): number {
  let a = 0;
  const n = ring.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += ring[i * 2] * ring[j * 2 + 1] - ring[j * 2] * ring[i * 2 + 1];
  }
  return a / 2;
}

function reverseRing(ring: number[]): number[] {
  const out: number[] = [];
  for (let i = ring.length - 2; i >= 0; i -= 2) out.push(ring[i], ring[i + 1]);
  return out;
}

// ---------------------------------------------------------------------------
// level crossings
// ---------------------------------------------------------------------------

/** One post at a level crossing, standing at the right-hand kerb of one approach. */
export interface CrossingPost {
  /** Which crossing, as an index into `world.rail.crossings`. */
  crossing: number;
  x: number;
  y: number;
  z: number;
  /** Unit vector towards the traffic it faces. */
  fx: number;
  fz: number;
  /** Unit vector from the kerb towards the middle of the road, which is where the boom goes. */
  ix: number;
  iz: number;
  barrier: boolean;
  lights: boolean;
  /** Boom length, from its pivot. */
  arm: number;
}

/** Height of the lamps, the boom's pivot, and how far the boom stands off the post. */
export const LAMP_HEIGHT = 2.35;
export const LAMP_SPACING = 0.23;
export const PIVOT_HEIGHT = 0.95;
export const PIVOT_OFFSET = 0.32;

/**
 * Where the gear stands: one post per approach, at the kerb on the driver's right, a few
 * metres short of the outermost rail — which is where a Polish crossing has it, and where a
 * half barrier closes the lane coming in while leaving the one going out open.
 */
export function crossingGear(crossing: LevelCrossing, terrain: Heightfield, index = 0): CrossingPost[] {
  const out: CrossingPost[] = [];
  const back = crossing.reach + 3.2;
  for (const dir of [1, -1]) {
    // Traffic moving along `dir · (dx,dz)` towards the crossing.
    const ux = crossing.dx * dir;
    const uz = crossing.dz * dir;
    // The driver's right, in this game's axes (see `car.ts`).
    const rx = -uz;
    const rz = ux;
    const side = crossing.half + 0.75;
    const x = crossing.x - ux * back + rx * side;
    const z = crossing.z - uz * back + rz * side;
    out.push({
      crossing: index,
      x,
      y: terrain.heightAt(x, z) + 0.15,
      z,
      fx: -ux,
      fz: -uz,
      ix: -rx,
      iz: -rz,
      barrier: crossing.barriers,
      lights: crossing.lights,
      arm: crossing.half + 0.45,
    });
  }
  return out;
}

function addCrossingPost(b: RailBuilder, p: CrossingPost): void {
  // The pole.
  b.box(p.x, p.y, p.z, 0.13, 3.5, 0.13, p.fx, p.fz, POST);
  // St Andrew's cross: two boards in red and white, facing the traffic.
  const cy = p.y + 3.05;
  for (const tilt of [1, -1]) {
    const angle = (tilt * 40 * Math.PI) / 180;
    const ax = Math.cos(angle);
    const ay = Math.sin(angle);
    const length = 1.15;
    const stripes = 5;
    for (let s = 0; s < stripes; s++) {
      const t0 = -length / 2 + (length * s) / stripes;
      const t1 = t0 + length / stripes;
      const color = s % 2 === 0 ? CROSS_RED : CROSS_WHITE;
      const w = 0.09;
      // Across the board, in the plane facing the traffic: `i` sideways, `y` up.
      const corner = (t: number, h: number): [number, number, number] => [
        p.x + p.fx * 0.09 + p.ix * (ax * t - ay * h),
        cy + ay * t + ax * h,
        p.z + p.fz * 0.09 + p.iz * (ax * t - ay * h),
      ];
      const dz = tilt * 0.002;
      const q = [corner(t0, -w), corner(t1, -w), corner(t1, w), corner(t0, w)].map(
        (c) => [c[0] + p.fx * dz, c[1], c[2] + p.fz * dz] as [number, number, number],
      );
      b.quadFacing(q[0], q[1], q[2], q[3], [p.fx, 0, p.fz], color, RAIL_SURFACE.plain);
    }
  }
  if (p.lights) {
    // The black backplate the two lamps sit in, and a hood over each.
    const ly = p.y + LAMP_HEIGHT;
    b.box(p.x + p.fx * 0.07, ly - 0.2, p.z + p.fz * 0.07, 0.04, 0.4, 0.8, p.fx, p.fz, DARK, true);
    for (const s of [-1, 1]) {
      const hx = p.x + p.fx * 0.16 + p.ix * s * LAMP_SPACING;
      const hz = p.z + p.fz * 0.16 + p.iz * s * LAMP_SPACING;
      b.box(hx, ly + 0.1, hz, 0.2, 0.03, 0.18, p.fx, p.fz, DARK);
    }
  }
  if (p.barrier) {
    // The drive the boom pivots on.
    const dx = p.x + p.ix * PIVOT_OFFSET;
    const dz = p.z + p.iz * PIVOT_OFFSET;
    b.box(dx, p.y, dz, 0.42, PIVOT_HEIGHT - 0.05, 0.42, p.fx, p.fz, DRIVE);
  }
}

// ---------------------------------------------------------------------------
// the shader
// ---------------------------------------------------------------------------

const GLSL_PARS = /* glsl */ `
uniform float railWet;
varying vec3 vRailWorld;
varying float vRailSurface;
varying vec2 vRailUv;

float rlHash( vec2 p ) {
	vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
	p3 += dot( p3, p3.yzx + 33.33 );
	return fract( ( p3.x + p3.y ) * p3.z );
}
float rlNoise( vec2 p ) {
	vec2 i = floor( p );
	vec2 f = fract( p );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( rlHash( i ), rlHash( i + vec2( 1.0, 0.0 ) ), f.x ),
		mix( rlHash( i + vec2( 0.0, 1.0 ) ), rlHash( i + vec2( 1.0, 1.0 ) ), f.x ), f.y );
}
float rlPulseI( float t, float a, float b ) { return floor( t ) * ( b - a ) + clamp( fract( t ), a, b ) - a; }
// The share of a pixel of width w inside [a, b] of a unit-period pattern.
float rlPulse( float x, float a, float b, float w ) {
	return ( rlPulseI( x + w, a, b ) - rlPulseI( x - w, a, b ) ) / ( 2.0 * w );
}
float rlBand( float p, float lo, float hi, float w ) {
	return clamp( ( min( p + w, hi ) - max( p - w, lo ) ) / ( 2.0 * w ), 0.0, 1.0 );
}

struct RailOut { vec3 albedo; float rough; float metal; };

RailOut railSurface() {
	RailOut o;
	vec3 base = vColor.rgb;
	float rough = 0.92;
	float metal = 0.0;
	float s = vRailSurface;
	vec2 uv = vRailUv;
	float px = max( length( fwidth( vRailWorld.xz ) ), 1e-4 );
	float fine = 1.0 - smoothstep( 0.03, 0.12, px );
	float mid = 1.0 - smoothstep( 0.15, 0.6, px );

	if ( s < 2.5 ) {
		// --- ballast, and the sleepers laid in it ------------------------------------------
		vec2 w = vRailWorld.xz;
		float stones = rlNoise( w * 9.0 ) * 0.6 + rlNoise( w * 23.0 ) * 0.4;
		vec3 col = base * ( 0.78 + 0.4 * mix( 0.5, stones, fine ) );
		col *= 0.92 + 0.16 * rlNoise( w * 0.4 );
		if ( s < 1.5 ) {
			// Rust and oil drip between the rails, darkest under the middle of the train.
			float between = 1.0 - smoothstep( 0.55, 0.8, abs( uv.x ) );
			col = mix( col, col * vec3( 0.62, 0.5, 0.42 ), between * 0.55 );
			bool timber = s > 0.5;
			float period = timber ? 0.65 : 0.6;
			float half_ = timber ? 0.12 : 0.13;
			float pw = max( px / period, 1e-3 ) * 0.5;
			float sleeper = rlPulse( uv.y / period + 0.5, 0.5 - half_ / period, 0.5 + half_ / period, pw );
			sleeper *= rlBand( uv.x, -1.3, 1.3, px * 0.5 );
			vec3 wood = vec3( 0.21, 0.15, 0.1 ) * ( 0.8 + 0.4 * rlHash( vec2( floor( uv.y / period ), 3.0 ) ) );
			vec3 concrete = vec3( 0.36, 0.355, 0.34 ) * ( 0.85 + 0.25 * rlHash( vec2( floor( uv.y / period ), 7.0 ) ) );
			col = mix( col, timber ? wood : concrete, sleeper );
			// The shadow either side of each rail.
			float shadow = rlBand( abs( uv.x ), 0.62, 0.82, px * 0.5 );
			col *= 1.0 - 0.35 * shadow * mid;
		}
		base = col;
		rough = 0.95;
	} else if ( s < 4.5 ) {
		// --- rail steel: a polished running band on the head, rust on the web -----------------
		if ( s < 3.5 ) {
			base = vec3( 0.46, 0.45, 0.44 );
			rough = 0.32;
			metal = 0.85;
		} else {
			base = base * 0.75;
			rough = 0.7;
			metal = 0.3;
		}
	} else if ( s < 5.5 ) {
		// --- crossing panels and street track: concrete slabs with the flangeways cut -------
		float pw = max( px, 1e-3 ) * 0.5;
		float joint = rlPulse( uv.y / 1.2, -0.012, 0.012, pw / 1.2 );
		vec3 col = base * ( 0.9 + 0.2 * rlHash( vec2( floor( uv.y / 1.2 ), 1.0 ) ) );
		col *= 0.92 + 0.12 * rlNoise( vRailWorld.xz * 6.0 ) * fine;
		col *= 1.0 - 0.4 * joint * mid;
		// The groove inside each rail a wheel flange runs in.
		float groove = rlBand( abs( uv.x ), 0.62, 0.68, px * 0.5 );
		col = mix( col, vec3( 0.03 ), groove );
		base = col;
		rough = 0.85;
	} else if ( s < 6.5 ) {
		// --- platform: concrete slabs, laid square -----------------------------------------
		vec2 q = uv;
		float pw = max( px, 1e-3 ) * 0.5;
		float joint = max( rlPulse( q.x / 1.5, -0.008, 0.008, pw / 1.5 ), rlPulse( q.y / 1.5, -0.008, 0.008, pw / 1.5 ) );
		vec3 col = base * ( 0.9 + 0.15 * rlHash( floor( q / 1.5 ) ) );
		col *= 1.0 - 0.3 * joint * mid;
		base = col;
		rough = 0.9;
	} else if ( s < 7.5 ) {
		// --- the platform edge: a white line and the yellow strip a cane can feel ---------
		float pw = max( px, 1e-3 ) * 0.5 / 0.9;
		float white = rlBand( uv.x, 0.0, 0.12, pw );
		float yellow = rlBand( uv.x, 0.35, 0.75, pw );
		float dots = rlPulse( uv.y / 0.06, 0.25, 0.75, max( px / 0.06, 1e-3 ) * 0.5 ) * fine;
		vec3 col = base;
		col = mix( col, vec3( 0.82, 0.82, 0.8 ), white );
		col = mix( col, vec3( 0.78, 0.62, 0.12 ) * ( 1.0 - 0.15 * dots ), yellow );
		base = col;
		rough = 0.8;
	}

	if ( railWet > 0.0 ) {
		base *= mix( 1.0, 0.65, railWet );
		rough = mix( rough, 0.35, railWet );
	}

	o.albedo = base;
	o.rough = clamp( rough, 0.02, 1.0 );
	o.metal = metal;
	return o;
}
`;

function applyRailShader(material: THREE.MeshStandardMaterial): { setWet(amount: number): void } {
  const uniforms = { railWet: { value: 0 } };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float railSurface;\nattribute vec2 railUv;\n' +
          'varying vec3 vRailWorld;\nvarying float vRailSurface;\nvarying vec2 vRailUv;',
      )
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n' +
          '\tvRailWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;\n' +
          '\tvRailSurface = railSurface;\n\tvRailUv = railUv;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${GLSL_PARS}\nvoid main() {`)
      .replace('#include <color_fragment>', '\tRailOut railOut = railSurface();\n\tdiffuseColor.rgb = railOut.albedo;')
      .replace('#include <roughnessmap_fragment>', '\tfloat roughnessFactor = railOut.rough;')
      .replace('#include <metalnessmap_fragment>', '\tfloat metalnessFactor = railOut.metal;');
  };
  material.customProgramCacheKey = () => 'railway';
  return {
    setWet(amount) {
      uniforms.railWet.value = Math.max(0, Math.min(1, amount));
    },
  };
}

// ---------------------------------------------------------------------------
// building it
// ---------------------------------------------------------------------------

type V3 = [number, number, number];

class RailBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly surfaces: number[] = [];
  private readonly uvs: number[] = [];
  private readonly color = new THREE.Color();

  get count(): number {
    return this.positions.length / 3;
  }

  tri(a: V3, b: V3, c: V3, color: number, surface: number, ua: [number, number], ub: [number, number], uc: [number, number]): void {
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    this.color.setHex(color, THREE.SRGBColorSpace);
    for (const [p, uv] of [[a, ua], [b, ub], [c, uc]] as const) {
      this.positions.push(p[0], p[1], p[2]);
      this.normals.push(nx, ny, nz);
      this.colors.push(this.color.r, this.color.g, this.color.b);
      this.surfaces.push(surface);
      this.uvs.push(uv[0], uv[1]);
    }
  }

  /** A quad whose front faces `facing`, whichever order its corners came in. */
  quadFacing(
    a: V3, b: V3, c: V3, d: V3, facing: V3, color: number, surface: number,
    ua: [number, number] = [0, 0], ub: [number, number] = [0, 0], uc: [number, number] = [0, 0], ud: [number, number] = [0, 0],
  ): void {
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    if (nx * facing[0] + ny * facing[1] + nz * facing[2] >= 0) {
      this.tri(a, b, c, color, surface, ua, ub, uc);
      this.tri(a, c, d, color, surface, ua, uc, ud);
    } else {
      this.tri(a, c, b, color, surface, ua, uc, ub);
      this.tri(a, d, c, color, surface, ua, ud, uc);
    }
  }

  /**
   * A box standing on (x, y, z), `w` across, `h` tall, `d` deep along the facing (fx, fz).
   * `noBottom` is the default for anything standing on the ground; a box in the air wants one.
   */
  box(x: number, y: number, z: number, w: number, h: number, d: number, fx: number, fz: number, color: number, bottom = false): void {
    // Axes: f forward (depth), r sideways (width).
    const rx = -fz;
    const rz = fx;
    const corner = (sf: number, sr: number, sy: number): V3 => [
      x + fx * (d / 2) * sf + rx * (w / 2) * sr,
      y + sy * h,
      z + fz * (d / 2) * sf + rz * (w / 2) * sr,
    ];
    const faces: [V3, V3, V3, V3, V3][] = [
      [corner(1, -1, 0), corner(1, 1, 0), corner(1, 1, 1), corner(1, -1, 1), [fx, 0, fz]],
      [corner(-1, -1, 0), corner(-1, 1, 0), corner(-1, 1, 1), corner(-1, -1, 1), [-fx, 0, -fz]],
      [corner(-1, 1, 0), corner(1, 1, 0), corner(1, 1, 1), corner(-1, 1, 1), [rx, 0, rz]],
      [corner(-1, -1, 0), corner(1, -1, 0), corner(1, -1, 1), corner(-1, -1, 1), [-rx, 0, -rz]],
      [corner(-1, -1, 1), corner(1, -1, 1), corner(1, 1, 1), corner(-1, 1, 1), [0, 1, 0]],
    ];
    if (bottom) faces.push([corner(-1, -1, 0), corner(1, -1, 0), corner(1, 1, 0), corner(-1, 1, 0), [0, -1, 0]]);
    for (const [a, b2, c, d2, n] of faces) this.quadFacing(a, b2, c, d2, n, color, RAIL_SURFACE.plain);
  }

  toGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    geometry.setAttribute('railSurface', new THREE.Float32BufferAttribute(this.surfaces, 1));
    geometry.setAttribute('railUv', new THREE.Float32BufferAttribute(this.uvs, 2));
    geometry.computeBoundingSphere();
    return geometry;
  }
}

function shade(color: number, k: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * k));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * k));
  const b = Math.min(255, Math.round((color & 0xff) * k));
  return (r << 16) | (g << 8) | b;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}


/**
 * Whether a point is on the track bed of a line at grade — for keeping a road bridge's piers
 * off the rails it spans, as `CarriagewayIndex` keeps them off the streets.
 */
export function trackBedAt(world: World): (x: number, z: number) => boolean {
  const reach = BED_HALF + SHOULDER + 0.6;
  const cell = 32;
  const grid = new Map<number, number[][]>();
  for (const line of world.rail?.lines ?? []) {
    if (line.deck) continue;
    for (let i = 2; i < line.pts.length; i += 2) {
      const seg = [line.pts[i - 2], line.pts[i - 1], line.pts[i], line.pts[i + 1]];
      for (let cx = Math.floor((Math.min(seg[0], seg[2]) - reach) / cell); cx <= Math.floor((Math.max(seg[0], seg[2]) + reach) / cell); cx++) {
        for (let cz = Math.floor((Math.min(seg[1], seg[3]) - reach) / cell); cz <= Math.floor((Math.max(seg[1], seg[3]) + reach) / cell); cz++) {
          const key = cx * 100003 + cz;
          const bucket = grid.get(key);
          if (bucket) bucket.push(seg);
          else grid.set(key, [seg]);
        }
      }
    }
  }
  return (x, z) => {
    for (const [ax, az, bx, bz] of grid.get(Math.floor(x / cell) * 100003 + Math.floor(z / cell)) ?? []) {
      const vx = bx - ax;
      const vz = bz - az;
      const len2 = vx * vx + vz * vz;
      let t = len2 > 1e-9 ? ((x - ax) * vx + (z - az) * vz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      if ((x - ax - vx * t) ** 2 + (z - az - vz * t) ** 2 < reach * reach) return true;
    }
    return false;
  };
}
